import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { switchMap, tap, catchError } from 'rxjs/operators';
import { StateBase } from 'app/state/state-base';
import { getDateRange } from 'app/shared/Utils/common.utils';
import { ReportingService } from '../reporting.service';
import GraphqlService from 'app/services/graphql.service';
import { generateDownloadableFile, getAllowedFields } from 'app/shared/functions';
import { GraphqlConnection, GraphqlResultInfo, GraphqlResultPageInfo } from 'app/shared/models/graphql';
import {
  BuildChartData,
  DrillData,
  Graphql,
  Report,
  ReportList,
  SummaryFormula as SummaryFormulaActions,
} from './actions';
import { ReportLayout, ReportType, ReportTypeQueryParamValues } from '../../reports/reports.types';
import {
  CreateReportModel,
  ReportLayoutQueryParams,
  ReportLayoutChart,
  ReportLayoutDefinition,
  TimeSeriesCadenceOptions,
  ReportLayoutSummaryFormula,
  ReportListTableModel,
} from '../reporting.types';
import { RelatedToType } from 'app/shared/enums';
import { sortByProp, toTimeSeriesFieldDef } from '../reporting.functions';
import { PaginatedResource } from '../../../shared/models/paginated-resource';
import { UserService } from 'app/layout/common/user/user.service';
import { SummaryFormulaService, SummaryFormula } from '@services/summary-formula.service';
import { forkJoin, Observable, of } from 'rxjs';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { TimeSeriesChartService } from '@shared/modules/time-series-charts/time-series-charts.service';
import { FieldDef } from '@core/models';

const SUBTOTAL_VALUE_TEXT = 'Subtotal';

export type LayoutsPage = PaginatedResource<ReportLayout<ReportLayoutDefinition>>;

export interface ReportingStateModel {
  layouts: LayoutsPage;
  selectedLayout: ReportLayout<ReportLayoutDefinition>;
  chartData: ReportLayoutChart[];
  graphqlResults: {
    rows: any[];
    pageInfo?: GraphqlResultPageInfo;
    timeSeries?: {
      columns: string[];
      isGrouped?: boolean;
    };
  };
  drillData: { rows: any[]; pageInfo?: GraphqlResultPageInfo };
  summaryFormulas: SummaryFormula[];
  isChartDataLoading: boolean;
  reportList: ReportListTableModel;
}

@State<ReportingStateModel>({
  name: new StateToken<ReportingStateModel>('reportingState'),
  defaults: {
    layouts: null,
    selectedLayout: null,
    chartData: null,
    graphqlResults: null,
    drillData: null,
    summaryFormulas: [],
    isChartDataLoading: false,
    reportList: null,
  },
})
@Injectable()
export class ReportingState extends StateBase {
  constructor(
    private readonly reportingService: ReportingService,
    private readonly summaryFormulaService: SummaryFormulaService,
    private readonly graphqlService: GraphqlService,
    private readonly _snackBar: MatSnackBar,
    private readonly userService: UserService,
    private readonly timeSeriesChartService: TimeSeriesChartService,
    private readonly router: Router,
  ) {
    super();
  }

  @Selector()
  static getLayouts(state: ReportingStateModel): ReportLayout<ReportLayoutDefinition>[] {
    return state.layouts.results;
  }

  @Selector()
  static getLayoutsPage(state: ReportingStateModel): LayoutsPage {
    return state.layouts;
  }

  @Selector()
  static getSelectedLayout(state: ReportingStateModel): ReportLayout<ReportLayoutDefinition> {
    return state.selectedLayout;
  }

  @Selector()
  static getChartData(state: ReportingStateModel): any {
    return state.chartData;
  }

  @Selector()
  static getDrillData(state: ReportingStateModel): any {
    return state.drillData;
  }

  @Selector()
  static getGraphqlResults(state: ReportingStateModel) {
    return state.graphqlResults;
  }

  @Selector()
  static getReportList(state: ReportingStateModel): ReportListTableModel {
    return state.reportList;
  }

  @Selector()
  static getSummaryFormulas(state: ReportingStateModel) {
    return state.summaryFormulas;
  }

  @Selector()
  static isChartDataLoading(state: ReportingStateModel): boolean {
    return state.isChartDataLoading;
  }

  @Action(Report.Delete)
  deleteReport(context: StateContext<ReportingStateModel>, { reportId }: { reportId: number }) {
    return this.reportingService.delete<any>(reportId, Number(this.account.id)).pipe(
      tap(() => {
        const state = context.getState();
        const requestedPageAfterDelete =
          state.layouts?.results?.length === 1 ? state.layouts?.page - 1 : state.layouts?.page;

        this.store.dispatch(
          new Report.Get({
            report_types: [
              ReportTypeQueryParamValues.Legacy,
              ReportTypeQueryParamValues.SingleObject,
              ReportTypeQueryParamValues.TimeSeries,
            ],
            ...this.addProvidedPageParamsOrFallback(requestedPageAfterDelete, state.layouts?.per_page),
          }),
        );
      }),
    );
  }

  @Action(Report.Get)
  getReports(context: StateContext<ReportingStateModel>, { filter }: { filter: ReportLayoutQueryParams }) {
    const requestParams: ReportLayoutQueryParams = {
      ...filter,
      ...this.addProvidedPageParamsOrFallback(filter?.page, filter?.per_page),
      account_id: Number(this.account.id),
    };

    if (filter?.created_by_me) {
      requestParams.created_by_me = true;
    }

    if (filter?.favorites) {
      requestParams.favorites = true;
    }

    return this.reportingService.getReports<any>({ ...requestParams }).pipe(
      tap((response) => {
        this.reportingService.updateReportBuilderOptionsBadges({
          allReportsCount: response?.all_reports?.count ?? 0,
          favoritesCount: response?.favorites?.count ?? 0,
          byMeCount: response?.created_by_me?.count ?? 0,
          reportBuildersCount: response?.report_builders?.count ?? 0,
          pdfReportsCount: response?.pdf_reports?.count ?? 0,
        });

        const state = context.getState();
        context.setState({
          ...state,
          layouts: {
            ...response,
          },
        });
      }),
    );
  }

  @Action(Report.GetById)
  getReportById(
    context: StateContext<ReportingStateModel>,
    { id, refreshChart }: { id: number; refreshChart: boolean },
  ) {
    return this.reportingService.getReportById<any>(id, Number(this.account.id)).pipe(
      switchMap((reportLayout) => {
        if (reportLayout.report_type === ReportType.TimeSeries) {
          return forkJoin([of(reportLayout), this.timeSeriesChartService.getTimeSeriesFields(false)]);
        }

        return forkJoin([of(reportLayout), of(null)]);
      }),
      tap(([reportLayout, timeSeriesFieldDefs]: [ReportLayout<any>, FieldDef[]]) => {
        const selectedLayout: ReportLayout<ReportLayoutDefinition> = {
          ...reportLayout,
          definition: JSON.parse(reportLayout.definition),
          filters: JSON.parse(reportLayout.filters),
        };

        const fieldDefs = this.store.selectSnapshot(
          ObjectsManagerState.getFieldsByObjectName(selectedLayout.definition.related_to_type, {
            addNestedFields: true,
            allowedFieldsOnly: true,
          }),
        );

        if (timeSeriesFieldDefs?.length) {
          fieldDefs.push(...timeSeriesFieldDefs.map((field) => toTimeSeriesFieldDef(field)));
        }

        const allowedFieldsInReport = fieldDefs
          .filter(({ name }) => selectedLayout.definition.columns.includes(name))
          .map(({ name }) => name);

        selectedLayout.definition.columns = selectedLayout?.definition?.columns?.filter((name) =>
          allowedFieldsInReport.includes(name),
        );

        context.patchState({ selectedLayout });

        if (selectedLayout.definition.visualizations?.length) {
          this.store.dispatch(
            new BuildChartData.Action(
              {
                reportLayoutId: reportLayout.id,
                charts: selectedLayout.definition.visualizations,
              },
              {
                refreshChart,
              },
            ),
          );
        }
      }),
    );
  }

  @Action(Report.Create)
  createReport(context: StateContext<ReportingStateModel>, { model }: { model: CreateReportModel }) {
    let filters: any = { fjson: '[]', sort: [] };
    const definition: ReportLayoutDefinition = {
      related_to_type: model.relatedToType,
      visualizations: [],
      columns: [],
    };
    const isTimeSeries = model.report_type === ReportType.TimeSeries;

    if (isTimeSeries) {
      definition.cadence = TimeSeriesCadenceOptions.Month;
      definition.groupBy = [];
      filters = { ...filters, timeSeries: { startDate: null, endDate: null } };
    }

    if (model.relatedToType === RelatedToType.Trail) {
      definition.trailInfo = {
        target_field: 'project_stage_id',
        related_to_type: RelatedToType.Project,
      };
    }

    return this.reportingService
      .create<any>({
        name: model.name,
        account_id: Number(this.account.id),
        report_type: model.report_type,
        definition: JSON.stringify(definition),
        filters: JSON.stringify(filters),
      })
      .pipe(
        tap((reportLayout) => {
          const state = context.getState();
          context.setState({
            ...state,
            selectedLayout: {
              ...reportLayout,
              definition: JSON.parse(reportLayout.definition),
              filters: JSON.parse(reportLayout.filters),
            },
          });
        }),
      );
  }

  @Action(Report.Update)
  updateReport(
    context: StateContext<ReportingStateModel>,
    {
      model,
      options,
    }: { model: ReportLayout<any>; options: { silent: boolean; dispatchBuildChart: boolean; refreshChart: boolean } },
  ) {
    const payload = {
      ...model,
      account_id: Number(this.account.id),
      definition: JSON.stringify({
        ...model.definition,
        visualizations: model.definition.visualizations.map((visualization) => ({
          ...visualization,
          title: model.name,
        })),
      }),
      filters: model.filters,
    };
    const updateState = (reportLayout) => {
      if (options.silent === true) {
        return;
      }
      const state = context.getState();

      const newState = {
        ...state,
        selectedLayout: {
          ...reportLayout,
          definition: JSON.parse(reportLayout.definition),
          filters: JSON.parse(reportLayout.filters),
        },
      };

      context.setState(newState);

      this.store.dispatch(
        new BuildChartData.Action(
          {
            reportLayoutId: reportLayout.id,
            charts: newState.selectedLayout.definition.visualizations,
          },
          { refreshChart: options.refreshChart ?? false },
        ),
      );
    };

    if (this.store.selectSnapshot(AccountState.getAccount)?.features.includes(FeatureFlag.SUMMARY_FORMULAS)) {
      let reportFormula = model.report_layout_summary_formulas.find((x) => x._destroy);
      if (model.report_layout_summary_formulas.find((x) => x.field === reportFormula?.field && !x._destroy)) {
        return this.toggleBetweenFormulas(payload, reportFormula).pipe(tap(updateState));
      }
    }

    return this.reportingService.update<any>(payload).pipe(tap(updateState));
  }

  @Action(Report.Duplicate)
  duplicateReport(
    context: StateContext<ReportingStateModel>,
    { model: { id } }: { model: ReportLayout<ReportLayoutDefinition> },
  ) {
    return this.reportingService.duplicate<any>(id).pipe(
      tap((reportLayout) => {
        const state = context.getState();
        const newState = {
          ...state,
          selectedLayout: {
            ...reportLayout,
            definition: JSON.parse(reportLayout.definition),
            filters: JSON.parse(reportLayout.filters),
          },
        };
        context.setState(newState);
      }),
    );
  }

  @Action(Report.ClearSelected)
  clearSelectedReport(context: StateContext<ReportingStateModel>) {
    context.setState({ ...context.getState(), selectedLayout: null, chartData: null, graphqlResults: null });
  }

  @Action(BuildChartData.Action)
  buildChartData(
    context: StateContext<ReportingStateModel>,
    payload: { buildChartDataParams: BuildChartData.Params; options: { refreshChart: boolean } },
  ) {
    const {
      buildChartDataParams: { reportLayoutId, charts },
      options,
    } = payload;

    const state = context.getState();
    context.setState({
      ...state,
      isChartDataLoading: true,
    });

    return this.reportingService.buildChartData(reportLayoutId, charts, options.refreshChart ?? false).pipe(
      tap((chartData) => {
        const currentState = context.getState();
        context.setState({
          ...currentState,
          chartData: chartData.reduce((p, c) => [...p, ...c], []),
          isChartDataLoading: false,
        });
      }),
      catchError(() => {
        const currentState = context.getState();
        context.setState({
          ...currentState,
          isChartDataLoading: false,
        });
        return of(null);
      }),
    );
  }

  @Action(DrillData.Action)
  drillData(context: StateContext<ReportingStateModel>, { grapqlParams }: { grapqlParams: Graphql.Params }) {
    return this.graphqlService
      .query({
        connection: grapqlParams.connection,
        fields: grapqlParams.fields,
        filter: grapqlParams.filter,
        fieldDefs: grapqlParams.fieldDefs,
        options: grapqlParams.options,
      })
      .pipe(
        tap((result) => {
          const { edges, pageInfo } = result;
          const state = context.getState();

          context.setState({
            ...state,
            drillData: {
              rows: edges.map(({ node }) => node),
              pageInfo: this.enrichGraphqlPageInfo(pageInfo, grapqlParams),
            },
          });
        }),
      );
  }

  @Action(Graphql.Action)
  graphql(context: StateContext<ReportingStateModel>, { graphqlParams }: { graphqlParams: Graphql.Params }) {
    let fields = [...graphqlParams.fields.filter((field) => field !== 'id'), 'id', 'name'];

    const params = { ...graphqlParams, fields };

    if (graphqlParams.relatedToType === RelatedToType.Project) {
      fields = [...fields.filter((field) => field !== 'pipeline_id'), 'pipeline_id'];

      fields = [...(fields.filter((field) => !['title', 'name'].includes(field)) ?? []), 'title'];
    }

    if (graphqlParams.relatedToType === RelatedToType.Engagement) {
      fields = fields.filter((name) => name !== 'name');
    }

    if (graphqlParams.relatedToType === RelatedToType.Task) {
      fields = fields.filter((name) => name !== 'name');
    }

    if (/*this.isParamsForProjectOrCO(graphqlParams) && */ graphqlParams.groupBy?.length) {
      params.format = 'json';
    }

    params.fields = fields;

    if (graphqlParams.connection.startsWith('co_')) {
      params.fields = params.fields.filter((field) => field !== 'name');
    }

    if (graphqlParams.connection !== GraphqlConnection.timeSeries) {
      delete params.relatedToType;
    } else {
      if (params.groupBy?.length) {
        params.format = graphqlParams.format ?? 'time_series_json';
      }
    }

    return this.graphqlService.query(params, graphqlParams.exportOptions).pipe(
      tap((result) => {
        if (graphqlParams.exportOptions) {
          let castedResult = result as any;
          if (castedResult?.message) {
            const snackBar = this._snackBar.open(castedResult.message, 'View exports', { duration: 3000 });
            snackBar.onAction().subscribe(() => {
              this.router.navigate(['/settings/jobs-status']);
            });
          } else {
            generateDownloadableFile(
              castedResult,
              graphqlParams.exportOptions.format,
              graphqlParams.exportOptions.fileName,
            );
          }
        } else {
          // Support both GraphqlResultInfo shape and raw GraphQL { data: { connection: { edges, pageInfo } } }
          let edges: any[] = [];
          let pageInfo: GraphqlResultPageInfo = null;

          const anyResult = result as any;
          if (anyResult?.data) {
            const dataObj = anyResult.data;
            const firstKey = Object.keys(dataObj)[0];
            const payload = dataObj[firstKey] || {};
            edges = payload?.edges ?? [];
            pageInfo = payload?.pageInfo ?? null;
          } else {
            ({ edges, pageInfo } = result as any);
          }

          const rows = (edges ?? []).map(({ node }) => node as any);

          const graphqlResults = {
            rows,
            pageInfo: this.enrichGraphqlPageInfo(pageInfo, graphqlParams),
            timeSeries: null,
          };

          // Only attempt time series normalization if service returned timeSeries payload
          if ((result as any)?.timeSeries) {
            this.transformTimeSeriesResults(graphqlParams, graphqlResults, result as any);
          }

          const state = context.getState();

          context.setState({
            ...state,
            graphqlResults,
          });
        }
      }),
    );
  }

  @Action(ReportList.Action)
  getReportList(context: StateContext<ReportingStateModel>, { params }: { params: ReportList.Params }) {
    return this.reportingService.getReportLayoutList(params.id, params.page, params.perPage).pipe(
      tap((response) => {
        const rows = (response.data ?? []).map((row) => this.transformReportListRow(response.headers, row));

        context.patchState({
          reportList: {
            headers: response.headers,
            rows,
            pageInfo: {
              filteredCount: response.pageInfo?.filtered_count ?? 0,
              currentPage: params.page,
              pageSize: params.perPage,
            },
          },
        });
      }),
    );
  }

  @Action(ReportList.Export)
  exportReportList(_context: StateContext<ReportingStateModel>, { params }: { params: ReportList.ExportParams }) {
    return this.reportingService.exportReportLayoutList(params.id, params.format).pipe(
      tap((result) => {
        if (result?.message) {
          const snackBar = this._snackBar.open(result.message, 'View exports', { duration: 3000 });
          snackBar.onAction().subscribe(() => {
            this.router.navigate(['/settings/jobs-status']);
          });
        }
      }),
    );
  }

  @Action(Report.ToggleFavorite)
  toggleFavorite(context: StateContext<ReportingStateModel>, { id }: { id: number }) {
    return this.userService.toggleFavoriteRecord<ReportLayout<any>>(Number(this.accountId), 'ReportLayout', id).pipe(
      tap(() => {
        const state = context.getState();
        context.patchState({
          layouts: {
            ...state.layouts,
            results: state.layouts.results.map((layout) => {
              if (layout.id === id) {
                layout.is_favorite = !layout.is_favorite;
              }
              return layout;
            }),
          },
        });
      }),
    );
  }

  @Action(SummaryFormulaActions.Get)
  getSummaryFormulas(context: StateContext<ReportingStateModel>) {
    return this.summaryFormulaService.getByAccountId(this.accountId).pipe(
      tap((response) => {
        context.setState({
          ...context.getState(),
          summaryFormulas: response,
        });
      }),
    );
  }

  private transformTimeSeriesResults(
    graphqlParams: Graphql.Params,
    graphqlResults: {
      rows: any[];
      pageInfo: GraphqlResultPageInfo;
      timeSeries: any;
    },
    rawResult: GraphqlResultInfo<any>,
  ): void {
    let dates = new Set<string>([]);
    const fields = graphqlParams.fields.map((f) => f.substring(0, f.indexOf('(')));
    if (graphqlParams.cadence === TimeSeriesCadenceOptions.Day) {
      dates = new Set<string>(getDateRange(graphqlParams.startDate, graphqlParams.endDate));
    } else if (graphqlParams.cadence === TimeSeriesCadenceOptions.Month) {
      dates = new Set<string>(getDateRange(graphqlParams.startDate, graphqlParams.endDate, true));
    } else {
      graphqlResults.rows.forEach((curr) => {
        Object.keys(curr)
          .filter((key) => fields.includes(key))
          .forEach((customFieldName) => {
            curr[customFieldName].reduce((prevTSData: any, currTSData: any) => dates.add(currTSData.date), null);
          });
      });
    }

    const fieldDefs = getAllowedFields(
      this.store.selectSnapshot(ObjectsManagerState.getAllFields),
      graphqlParams.relatedToType,
      true,
    );
    const tsFieldIndex = rawResult.timeSeries.headers.findIndex((x) => x === 'time_series_field');
    let columns = rawResult.timeSeries.headers.map((headerStr, i) => {
      // it's for backward compatibility
      headerStr =
        Object.keys(headerStr).length === 1
          ? Object.keys(headerStr)[0] === ''
            ? Object.values(headerStr)[0]
            : Object.keys(headerStr)[0]
          : headerStr;

      let field = fieldDefs.find((fieldDef) => fieldDef.name === headerStr) ?? {
        name: headerStr,
        label: headerStr === 'time_series_field' ? 'Time Series Field' : headerStr,
        isDraggable: !(i >= tsFieldIndex || headerStr === 'Total'),
      };

      if (graphqlParams.connection === GraphqlConnection.timeSeries && headerStr === 'title') {
        (field as any).isDraggable = false;
      }

      if (headerStr.includes('.')) {
        const parentField = fieldDefs.find((x) => x.name === headerStr.split('.')[0]);
        field.label = `${parentField.label} ${field.label}`;
      }

      return field;
    });

    const rows = this.transformForTimeSeriesJson(
      rawResult.timeSeries.data,
      columns.map((x) => x.name),
    );

    if (!graphqlParams.fields.includes('id')) {
      columns = columns.filter((x) => x.name !== 'id');
    }

    if (!graphqlParams.fields.includes('pipeline_id')) {
      columns = columns.filter((x) => x.name !== 'pipeline_id');
    }

    columns = columns.filter((x) => x.name !== 'meta');

    graphqlResults.rows = rows.edges.map((x) => x.node);

    const hasSubtotal = rawResult.timeSeries.data.some((row) =>
      row.some((rowValue) => rowValue === SUBTOTAL_VALUE_TEXT),
    );
    const hasTotal = rawResult.timeSeries.data.some((row) => row.some((rowValue) => rowValue === 'Total'));

    // Ensure special rows (Subtotal/Total) carry a valid fieldDefName so pipes can format values
    if (hasSubtotal || hasTotal) {
      const fallbackFieldDefName = graphqlResults.rows[0]?.fieldDefName;

      graphqlResults.rows = graphqlResults.rows.map((row: any) => {
        const values = Object.values(row);
        const isSubtotalLine = values.includes(SUBTOTAL_VALUE_TEXT);
        const isTotalLine = values.includes('Total') || row?.Total === 'Total';

        if ((isSubtotalLine || isTotalLine) && !row.fieldDefName) {
          row.fieldDefName = fallbackFieldDefName;
        }

        return row;
      });
    }

    graphqlResults.timeSeries = { columns, isGrouped: true };
  }

  private transformForTimeSeriesJson(data: any[][], headers: string[]): GraphqlResultInfo<any> {
    const timeSerieFieldIndex = headers.findIndex((x) => x === 'time_series_field');
    const result: GraphqlResultInfo<any> = {
      edges: data
        .map((rowArray) => {
          return rowArray.reduce((object, columnValue, index) => {
            return { ...object, [headers[index]]: columnValue, fieldDefName: rowArray[timeSerieFieldIndex] };
          }, {});
        })
        .map((x, i) => {
          return { cursor: String(i), node: x };
        }),
      pageInfo: null,
    };

    return result;
  }

  private buildTimeSeriesColumnsArray(cadence: string, dates: Set<string>, fields: string[]): string[] {
    let result = [] as string[];

    switch (cadence) {
      case TimeSeriesCadenceOptions.Quarter:
        result = [...dates]
          .map((x) => ({ q: x.substring(5), y: x.substring(0, 4) }))
          .sort((a, b) => sortByProp(a, b, 'q'))
          .sort((a, b) => sortByProp(a, b, 'y'))
          .map((x) => `${x.y}-${x.q}`);
        break;
      case TimeSeriesCadenceOptions.Month:
        result = [...dates]
          .map((x) => ({ m: x.substring(5), y: x.substring(0, 4) }))
          .sort((a, b) => sortByProp(a, b, 'm'))
          .sort((a, b) => sortByProp(a, b, 'y'))
          .map((x) => `${x.y}-${x.m}`);
        break;
      case TimeSeriesCadenceOptions.Year:
        result = [...dates]
          .map((x) => +x)
          .sort()
          .map((x) => String(x));
        break;
      default:
        result = [...dates];
    }

    return [...fields, ...result];
  }

  private transformReportListRow(headers: string[], row: any[]): Record<string, any> {
    const result: Record<string, any> = {};
    const trailingValue = row[row.length - 1];
    const isDateValueArray =
      Array.isArray(trailingValue) &&
      trailingValue.every((entry) => entry && typeof entry === 'object' && 'date' in entry && 'value' in entry);

    if (!isDateValueArray) {
      headers.forEach((header, index) => {
        result[header] = row[index];
      });
      return result;
    }

    headers.slice(0, row.length - 1).forEach((header, index) => {
      result[header] = row[index];
    });

    trailingValue.forEach(({ date, value }) => {
      result[date] = value;
    });

    return result;
  }

  private addProvidedPageParamsOrFallback(page?: number, per_page?: number): { page: number; per_page: number } {
    return {
      page: page ?? 1,
      per_page: per_page ?? 20,
    };
  }

  private enrichGraphqlPageInfo(
    pageInfo: GraphqlResultPageInfo | null,
    graphqlParams: Graphql.Params,
  ): GraphqlResultPageInfo | null {
    if (!pageInfo) {
      return pageInfo;
    }

    const { page, per_page } = this.addProvidedPageParamsOrFallback(
      graphqlParams.filter?.pagination?.page,
      graphqlParams.filter?.pagination?.size,
    );

    return {
      ...pageInfo,
      currentPage: pageInfo.currentPage ?? page,
      pageSize: pageInfo.pageSize ?? per_page,
      filteredCount: pageInfo.filteredCount ?? 0,
    };
  }

  private toggleBetweenFormulas(
    payload: ReportLayout<any>,
    reportFormula: ReportLayoutSummaryFormula,
  ): Observable<ReportLayout<any>> {
    return this.reportingService
      .update<any>({
        id: payload.id,
        account_id: Number(this.account.id),
        report_layout_summary_formulas: [reportFormula],
      })
      .pipe(
        switchMap(() => {
          payload.report_layout_summary_formulas = payload.report_layout_summary_formulas.filter((x) => !x._destroy);
          return this.reportingService.update<any>(payload);
        }),
      );
  }
}
